"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Container } from "@/components/ui/container";
import { useAuth } from "@/components/providers/auth-provider";
import { useLibrary } from "@/features/dashboard/hooks";
import {
  coalesceCourseMoney,
  formatCourseMoneyEnIn,
  getLocalizedField,
  type Course,
  type CoursePrice,
} from "../api";
import type { Locale } from "@/lib/i18n";
import { useCoursePricing } from "./CoursePricingProvider";
import { CourseEnrollCta } from "./CourseEnrollCta";
import { CourseCohortLine } from "./CourseCohortLine";
import { useScrolledPast } from "../lib/use-scrolled-past";
import { useCoursePlanPricingUiModel } from "../hooks/use-course-plan-pricing-ui-model";
import {
  formatTierUpgradeCtaLabel,
  isPlanIdActiveUpgradeTarget,
  resolvePlanPricingUiKind,
  resolvePricingUiOwnedState,
} from "../lib/resolve-course-plan-pricing-ui-state";
import { getOwnershipCopy } from "../lib/ownership-copy";
import { useCourseEligibilityStatus } from "../use-course-eligibility-status";
import {
  CourseDesktopSectionPopover,
  type CoursePageSection,
} from "./CourseSectionNavigator";
import { signInHref } from "@/lib/post-login-redirect";

/** `CoursePricingBlock`'s card; the bar shows once it has scrolled away (`SITE-CRS01`). */
const PRICING_CARD_ID = "course-pricing-sidebar";
const LG_MEDIA = "(min-width: 1024px)";

function formatCompactPrice(price: CoursePrice): string {
  if (price.isFree) return "Free";
  const list = coalesceCourseMoney(price.list);
  const sale = price.sale;
  const amount =
    sale !== undefined && Number.isFinite(sale) && sale < list ? sale : list;
  const symbol = price.currency === "BDT" ? "৳" : "$";
  return `${symbol}${formatCourseMoneyEnIn(amount)}`;
}

type Props = {
  courseId: string;
  slug: string;
  fallbackPrice: CoursePrice;
  eligibility?: Course["eligibility"];
  locale: Locale;
  firstLessonId: string | null;
  sections: CoursePageSection[];
  showPurchaseUi: boolean;
  /** The course title, localized. */
  title: string;
  /** The course's earliest cohort; the chosen plan's own date wins. */
  nextCohortStartAt?: string | null;
};

export function CourseDesktopStickyEnrollPill({
  courseId,
  slug,
  fallbackPrice,
  eligibility,
  locale,
  firstLessonId,
  sections,
  showPurchaseUi,
  title,
  nextCohortStartAt,
}: Props) {
  const t = useTranslations("courseDetail");
  const { plans, selectedPlan, selectedPlanId, mode } = useCoursePricing();
  const { isAuthenticated, getToken } = useAuth();
  const hasEligibilityGate = !!eligibility;
  const { data: eligibilityStatus } = useCourseEligibilityStatus(
    { slug, eligibility },
    isAuthenticated && hasEligibilityGate,
  );
  const eligibilityPassed =
    hasEligibilityGate && isAuthenticated && eligibilityStatus?.passed === true;
  const eligibilityLocked = hasEligibilityGate && !eligibilityPassed;
  const eligibilityQuizSlug =
    eligibilityStatus?.quiz?.slug ?? eligibility?.quizSlug ?? "";
  const eligibilityAttemptHref = `/${locale}/quiz-exams/${eligibilityQuizSlug}/attempt`;
  const eligibilityCtaHref = isAuthenticated
    ? eligibilityAttemptHref
    : signInHref(locale, eligibilityAttemptHref);

  const token = getToken();
  const { data: library } = useLibrary(token ?? undefined);
  const ownedCourse = library?.courses.find((c) => c.courseId === courseId);
  const isOwned = !!ownedCourse;

  const pricingUiModel = useCoursePlanPricingUiModel(
    ownedCourse,
    isAuthenticated,
    plans,
  );

  const selectedIsUpgradeTarget =
    !!selectedPlan && isPlanIdActiveUpgradeTarget(selectedPlan._id, pricingUiModel);

  const planName =
    getLocalizedField(
      selectedPlan?.name_en,
      selectedPlan?.name_bn,
      locale,
    ) ??
    selectedPlan?.name_en ??
    "";
  const copy = getOwnershipCopy(locale);

  const selectedPlanOwnedState =
    selectedPlan && isAuthenticated && isOwned
      ? resolvePricingUiOwnedState(selectedPlan, pricingUiModel, {
          isAuthenticated,
          isOwned,
        })
      : null;

  const nominalLegacyPlan = pricingUiModel.nominalLegacyPlan;
  const nominalLegacyName =
    nominalLegacyPlan &&
    (getLocalizedField(
      nominalLegacyPlan.name_en,
      nominalLegacyPlan.name_bn,
      locale,
    ) ??
      nominalLegacyPlan.name_en);

  const ownedTierChipName =
    pricingUiModel.effectiveOwnedPlanForCopy &&
    (getLocalizedField(
      pricingUiModel.effectiveOwnedPlanForCopy.name_en,
      pricingUiModel.effectiveOwnedPlanForCopy.name_bn,
      locale,
    ) ??
      pricingUiModel.effectiveOwnedPlanForCopy.name_en);

  const stickyPeerNotice =
    selectedPlan && selectedPlanOwnedState
      ? selectedPlanOwnedState.kind === "owned-other" && !selectedPlanOwnedState.upgradeable
        ? t("planOwnership.coveredByPlan", {
            planName: ownedTierChipName ?? t("planOwnership.yourPlanShort"),
          })
        : selectedPlanOwnedState.kind === "owned-legacy-peer" && nominalLegacyName
          ? t("planOwnership.enrolledInPlanChip", { planName: nominalLegacyName })
          : undefined
      : undefined;

  const upgradePathBlocked =
    mode === "multi" &&
    selectedPlanId !== null &&
    !!isAuthenticated &&
    !!isOwned &&
    !!selectedPlan &&
    resolvePlanPricingUiKind(selectedPlan, pricingUiModel, {
      isAuthenticated,
      isOwned,
    }) === "LOWER_OR_SIDEWAYS";

  const upgradeUnavailableReason = stickyPeerNotice
    ? undefined
    : upgradePathBlocked
      ? "This tier is not offered as an upgrade from your current access. You can continue learning on your included plan or pick an upgrade tier listed for your level."
      : undefined;
  const upgradeUnavailableLabel = stickyPeerNotice
    ? undefined
    : upgradePathBlocked
      ? `${copy.upgradePrefix} ${planName}`
      : undefined;

  const [isLg, setIsLg] = useState(false);
  const pastCard = useScrolledPast([PRICING_CARD_ID], isLg);

  useEffect(() => {
    const mq = window.matchMedia(LG_MEDIA);
    const onChange = () => setIsLg(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // The chosen plan's own date; the course-wide one only for a course sold without
  // plans (a plan with no date must not borrow another plan's cohort).
  const cohortIso = mode === "legacy" ? (nextCohortStartAt ?? null) : (selectedPlan?.cohortStartAt ?? null);
  const showPurchaseChrome = showPurchaseUi && mode !== "loading";
  const showChrome = isLg && pastCard && (sections.length > 0 || showPurchaseChrome);

  const activePlanPrice = selectedPlan?.price ?? fallbackPrice;
  const upgradeLabel =
    selectedIsUpgradeTarget && selectedPlan
      ? formatTierUpgradeCtaLabel(selectedPlan, pricingUiModel, {
          upgradePay: (amount) => t("planOwnership.upgradePay", { amount }),
          upgradeFree: () => t("planOwnership.upgradeFree"),
        })
      : undefined;
  const upgradeToPlanId = selectedIsUpgradeTarget ? selectedPlan?._id : undefined;

  const ownedNoUpgrade = isAuthenticated && isOwned && !pricingUiModel.hasActiveUpgradeTarget;
  const priceLine = ownedNoUpgrade ? (
    <p className="truncate text-[14px] font-semibold text-emerald-300">{copy.legacyBannerTitle}</p>
  ) : mode === "legacy" || selectedPlan ? (
    <p className="flex flex-col leading-tight">
      {mode === "multi" && planName ? (
        <span className="max-w-[220px] truncate text-[12px] font-medium text-neutral-400">{planName}</span>
      ) : null}
      <span className="text-[18px] font-bold tabular-nums text-white">
        {formatCompactPrice(mode === "legacy" ? fallbackPrice : activePlanPrice)}
      </span>
    </p>
  ) : null;

  const cta = !showPurchaseChrome ? null : eligibilityLocked ? (
    <Button className="h-11 shrink-0 rounded-[10px] px-5 text-[15px] font-semibold" asChild>
      <Link href={eligibilityCtaHref}>{t("eligibilityCtaPrimary")}</Link>
    </Button>
  ) : mode === "legacy" ? (
    <CourseEnrollCta
      courseId={courseId}
      slug={slug}
      price={fallbackPrice}
      locale={locale}
      firstLessonId={firstLessonId}
      enrollLabel={fallbackPrice.isFree ? undefined : t("enrollNow")}
      size="sm"
      className="h-11 w-auto min-w-[9rem] shrink-0 px-6"
    />
  ) : selectedPlan ? (
    <CourseEnrollCta
      courseId={courseId}
      slug={slug}
      price={activePlanPrice}
      pricingPlanId={selectedPlan._id}
      enrollLabel={activePlanPrice.isFree ? undefined : t("enrollNow")}
      upgradeToPlanId={upgradeToPlanId}
      upgradeLabel={upgradeLabel}
      upgradeUnavailableReason={upgradeUnavailableReason}
      upgradeUnavailableLabel={upgradeUnavailableLabel}
      enrolledPeerNotice={stickyPeerNotice}
      locale={locale}
      firstLessonId={firstLessonId}
      size="sm"
      className="h-11 w-auto min-w-[9rem] shrink-0 px-6"
    />
  ) : null;

  return (
    <div
      role="region"
      aria-label={t("stickyPillAriaLabel")}
      aria-hidden={!showChrome}
      inert={!showChrome}
      data-bottom-bar="wide"
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 hidden border-t border-white/10 bg-neutral-950 text-white shadow-[0_-8px_30px_rgba(0,0,0,0.14)] transition-transform duration-200 ease-out motion-reduce:transition-none lg:block",
        showChrome ? "translate-y-0" : "pointer-events-none translate-y-full",
      )}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <Container className="flex h-[72px] items-center gap-5">
        <div className="flex min-w-0 flex-1 items-center gap-5">
          <span className="truncate text-[15px] font-semibold">{title}</span>
          <CourseCohortLine iso={cohortIso} tone="bar" className="hidden shrink-0 xl:inline" />
        </div>
        <CourseDesktopSectionPopover sections={sections} tone="dark" placement="up" />
        {showPurchaseChrome ? (
          <>
            <div className="shrink-0 text-right">{priceLine}</div>
            <div className="shrink-0">{cta}</div>
          </>
        ) : null}
      </Container>
    </div>
  );
}
